import { getStateInvariantViolations } from "../invariants/game-state-invariants";
import {
  createSeededRandom,
  nextRandomInt,
  shuffleDeterministically,
} from "../random/seeded-random";
import { getStateHash } from "../replay/state-hash";
import { normalizeResourcePool, RESOURCE_KEYS } from "../resources/resource-pool";
import { createEmptyResourcePool, createEmptyTurnActionUsage } from "../state/state-helpers";
import {
  LANE_IDS,
  PLAYER_IDS,
  type CardDefinitionId,
  type CardInstanceId,
  type CardInstanceRecord,
  type CreateMatchResult,
  type EngineCardCatalog,
  type EngineCardCatalogV1,
  type EngineCardCatalogV2,
  type EngineCardCatalogV3,
  type EngineCardCatalogV4,
  type EngineCardDefinitionV4,
  FLANK_KINGDOM_COMPLETE_RULES_VERSION,
  type GameEvent,
  type GameStateV2,
  type GameStateV4,
  type GameStateV5,
  type MatchSetupFailure,
  type MatchSetupInput,
  type OpeningHandAudit,
  type PlayerId,
  type PlayerLaneState,
  type PlayerState,
  type SeededRandomState,
  type SetupGameState,
  PUBLIC_BETA_RULES_VERSION,
} from "../types";

export const REQUIRED_SETUP_DECK_SIZE = 40 as const;
export const MAX_SETUP_CARD_COPIES = 3 as const;
export const MAX_OPENING_HAND_ATTEMPTS = 10_000 as const;
export const SUPPORTED_SETUP_RULES_VERSION = "first-edition" as const;
export const SUPPORTED_FLANK_RULES_VERSION = FLANK_KINGDOM_COMPLETE_RULES_VERSION;
export const SUPPORTED_PUBLIC_BETA_RULES_VERSION = PUBLIC_BETA_RULES_VERSION;

interface OpeningHandResult {
  readonly ok: true;
  readonly deck: readonly CardInstanceId[];
  readonly hand: readonly CardInstanceId[];
  readonly audit: OpeningHandAudit;
  readonly random: SeededRandomState;
}

interface OpeningHandFailure {
  readonly ok: false;
  readonly failure: MatchSetupFailure;
}

function setupFailure(
  code: MatchSetupFailure["code"],
  message: string,
  path: string | null,
  details: MatchSetupFailure["details"] = {},
): MatchSetupFailure {
  return { code, message, path, details };
}

function setupEventId(ordinal: number): string {
  return `setup-event-0-${ordinal}`;
}

function emptyLaneSide(): PlayerLaneState {
  return {
    status: "empty",
    land: null,
    commander: null,
    armies: [],
    conspiracyCardInstanceId: null,
    attachedCardInstanceIds: [],
    combatModifiers: [],
  };
}

function createPlayer(
  playerId: PlayerId,
  deck: readonly CardInstanceId[],
  hand: readonly CardInstanceId[],
): PlayerState {
  return {
    playerId,
    deck,
    hand,
    discard: [],
    exhaust: [],
    eventResolution: [],
    activePolicyId: null,
    victoryBanners: 0,
    actionsUsed: createEmptyTurnActionUsage(),
    kingdomPool: createEmptyResourcePool(),
  };
}

function definitionMap(catalog: EngineCardCatalog) {
  return new Map(catalog.cards.map((definition) => [definition.id, definition]));
}

function cloneCatalogV1(catalog: EngineCardCatalogV1): EngineCardCatalogV1 {
  return {
    ...catalog,
    cards: catalog.cards.map((definition) =>
      definition.type === "land"
        ? {
            ...definition,
            stages: [
              { ...definition.stages[0] },
              { ...definition.stages[1] },
              { ...definition.stages[2] },
            ],
          }
        : { ...definition },
    ),
  };
}

function cloneCatalogV2(catalog: EngineCardCatalogV2): EngineCardCatalogV2 {
  return {
    ...catalog,
    cards: catalog.cards.map((definition) => {
      if (definition.type === "land") {
        return {
          ...definition,
          stages: [
            {
              ...definition.stages[0],
              production: { ...definition.stages[0].production },
            },
            {
              ...definition.stages[1],
              production: { ...definition.stages[1].production },
            },
            {
              ...definition.stages[2],
              production: { ...definition.stages[2].production },
            },
          ],
        };
      }
      if (definition.type === "army") {
        return { ...definition, upkeep: { ...definition.upkeep } };
      }
      return { ...definition };
    }),
  };
}

function cloneCatalogV3(catalog: EngineCardCatalogV3): EngineCardCatalogV3 {
  return {
    ...catalog,
    cards: catalog.cards.map((definition) => {
      if (definition.type === "land") {
        return {
          ...definition,
          cost: { ...definition.cost },
          stages: [
            {
              ...definition.stages[0],
              production: { ...definition.stages[0].production },
            },
            {
              ...definition.stages[1],
              production: { ...definition.stages[1].production },
            },
            {
              ...definition.stages[2],
              production: { ...definition.stages[2].production },
            },
          ],
        };
      }
      if (definition.type === "army") {
        return {
          ...definition,
          cost: { ...definition.cost },
          upkeep: { ...definition.upkeep },
        };
      }
      if (definition.type === "commander") {
        return {
          ...definition,
          cost: { ...definition.cost },
          mandates: definition.mandates.map((mandate) => ({
            ...mandate,
            condition: { ...mandate.condition },
            effects: mandate.effects.map((effect) => ({ ...effect })),
          })),
        };
      }
      return { ...definition, cost: { ...definition.cost } };
    }),
  };
}

function cloneCatalogV4(catalog: EngineCardCatalogV4): EngineCardCatalogV4 {
  return {
    ...catalog,
    cards: catalog.cards.map((definition) => {
      if (definition.type === "land") {
        return {
          ...definition,
          cost: { ...definition.cost },
          stages: [
            {
              ...definition.stages[0],
              production: { ...definition.stages[0].production },
            },
            {
              ...definition.stages[1],
              production: { ...definition.stages[1].production },
            },
            {
              ...definition.stages[2],
              production: { ...definition.stages[2].production },
            },
          ],
        };
      }
      if (definition.type === "army") {
        return {
          ...definition,
          cost: { ...definition.cost },
          upkeep: { ...definition.upkeep },
        };
      }
      if (definition.type === "commander") {
        return {
          ...definition,
          cost: { ...definition.cost },
          mandates: definition.mandates.map((mandate) => ({
            ...mandate,
            condition: { ...mandate.condition },
            effects: mandate.effects.map((effect) => ({ ...effect })),
          })),
        };
      }
      if (definition.type === "event") {
        return {
          ...definition,
          cost: { ...definition.cost },
          effects: definition.effects.map((effect) =>
            effect.type === "gain-kingdom-resources"
              ? { ...effect, resources: { ...effect.resources } }
              : { ...effect },
          ),
        };
      }
      return { ...definition, cost: { ...definition.cost } };
    }),
  };
}

function cloneEngineCardCatalog(catalog: EngineCardCatalog): EngineCardCatalog {
  return catalog.schemaVersion === 1
    ? cloneCatalogV1(catalog)
    : catalog.schemaVersion === 2
      ? cloneCatalogV2(catalog)
      : catalog.schemaVersion === 3
        ? cloneCatalogV3(catalog)
        : cloneCatalogV4(catalog);
}

function hasCompleteResourcePool(value: unknown): value is Readonly<Record<string, number>> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length === RESOURCE_KEYS.length &&
    RESOURCE_KEYS.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
}

function hasValidMandateReferences(value: unknown): boolean {
  if (!Array.isArray(value) || value.length === 0) return false;

  return value.every((candidate: unknown) => {
    if (typeof candidate !== "object" || candidate === null) return false;
    const mandate = candidate as Readonly<Record<string, unknown>>;
    const condition = mandate.condition;
    const effects = mandate.effects;
    return (
      typeof mandate.mandateId === "string" &&
      mandate.mandateId.length > 0 &&
      typeof mandate.name === "string" &&
      mandate.name.length > 0 &&
      typeof condition === "object" &&
      condition !== null &&
      typeof (condition as Readonly<Record<string, unknown>>).conditionId === "string" &&
      ((condition as Readonly<Record<string, unknown>>).conditionId as string).length > 0 &&
      Array.isArray(effects) &&
      effects.length > 0 &&
      effects.every(
        (effect: unknown) =>
          typeof effect === "object" &&
          effect !== null &&
          typeof (effect as Readonly<Record<string, unknown>>).effectId === "string" &&
          ((effect as Readonly<Record<string, unknown>>).effectId as string).length > 0,
      )
    );
  });
}

function validateCatalog(input: MatchSetupInput): MatchSetupFailure | null {
  const receivedCatalogSchemaVersion = (input.catalog as { readonly schemaVersion: number })
    .schemaVersion;
  if (
    input.matchId.length === 0 ||
    input.seed.length === 0 ||
    input.rulesVersion.length === 0 ||
    input.cardDataVersion.length === 0 ||
    input.gameplayCatalogHash.length === 0
  ) {
    return setupFailure(
      "INVALID_MATCH_SETUP",
      "Match ID, seed, rules version, card-data version, and catalog hash must be nonempty.",
      null,
    );
  }

  if (
    input.rulesVersion !== SUPPORTED_SETUP_RULES_VERSION &&
    input.rulesVersion !== SUPPORTED_FLANK_RULES_VERSION &&
    input.rulesVersion !== SUPPORTED_PUBLIC_BETA_RULES_VERSION
  ) {
    return setupFailure(
      "INVALID_MATCH_SETUP",
      `Unsupported setup rules version '${input.rulesVersion}'.`,
      "rulesVersion",
      {
        supportedRulesVersions: `${SUPPORTED_SETUP_RULES_VERSION},${SUPPORTED_FLANK_RULES_VERSION},${SUPPORTED_PUBLIC_BETA_RULES_VERSION}`,
      },
    );
  }

  if (
    receivedCatalogSchemaVersion !== 1 &&
    receivedCatalogSchemaVersion !== 2 &&
    receivedCatalogSchemaVersion !== 3 &&
    receivedCatalogSchemaVersion !== 4
  ) {
    return setupFailure(
      "INVALID_MATCH_SETUP",
      `Unsupported engine catalog schema version '${String(receivedCatalogSchemaVersion)}'.`,
      "catalog.schemaVersion",
      { supportedCatalogSchemaVersions: "1,2,3,4" },
    );
  }

  if (
    input.catalog.cardDataVersion !== input.cardDataVersion ||
    input.catalog.gameplayCatalogHash !== input.gameplayCatalogHash
  ) {
    return setupFailure(
      "CATALOG_HASH_MISMATCH",
      "Setup metadata must match the supplied engine catalog.",
      "gameplayCatalogHash",
      {
        setupCardDataVersion: input.cardDataVersion,
        catalogCardDataVersion: input.catalog.cardDataVersion,
        setupCatalogHash: input.gameplayCatalogHash,
        catalogHash: input.catalog.gameplayCatalogHash,
      },
    );
  }

  const seen = new Set<CardDefinitionId>();
  for (let index = 0; index < input.catalog.cards.length; index += 1) {
    const definition = input.catalog.cards[index];
    if (definition === undefined || definition.id.length === 0 || seen.has(definition.id)) {
      return setupFailure(
        "INVALID_MATCH_SETUP",
        "Engine catalog card definitions must have unique nonempty IDs.",
        `catalog.cards.${index}.id`,
      );
    }
    seen.add(definition.id);

    if (input.catalog.schemaVersion === 3 || input.catalog.schemaVersion === 4) {
      try {
        if (!("cost" in definition) || !hasCompleteResourcePool(definition.cost)) {
          throw new RangeError("Printed cost must include all five resource keys.");
        }
        normalizeResourcePool(definition.cost);
      } catch (error) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          error instanceof Error ? error.message : "Invalid printed card cost.",
          `catalog.cards.${index}.cost`,
        );
      }
    }

    if (definition.type === "land") {
      if (
        definition.developmentId.length === 0 ||
        definition.stages.length !== 3 ||
        definition.stages.some(
          (stage, stageIndex) =>
            stage.stage !== stageIndex + 1 ||
            !Number.isSafeInteger(stage.maximumControl) ||
            stage.maximumControl <= 0 ||
            (stageIndex > 0 &&
              stage.maximumControl < (definition.stages[stageIndex - 1]?.maximumControl ?? 0)),
        )
      ) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Projected Land definitions require Stages I-III with nondecreasing positive Control.",
          `catalog.cards.${index}.stages`,
        );
      }

      if (
        input.catalog.schemaVersion === 2 ||
        input.catalog.schemaVersion === 3 ||
        input.catalog.schemaVersion === 4
      ) {
        if (
          !("landTypeId" in definition) ||
          !/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/.test(definition.landTypeId) ||
          definition.landTypeId.length > 64
        ) {
          return setupFailure(
            "INVALID_MATCH_SETUP",
            "Projected schema-2/3 Lands require a stable lowercase kebab-case Land Type ID.",
            `catalog.cards.${index}.landTypeId`,
          );
        }
        if (definition.developmentId !== definition.id) {
          return setupFailure(
            "INVALID_MATCH_SETUP",
            "Projected schema-2/3 Land development identity must equal its card definition ID.",
            `catalog.cards.${index}.developmentId`,
          );
        }
        try {
          definition.stages.forEach((stage) => {
            if (!hasCompleteResourcePool(stage.production)) {
              throw new RangeError("Land production must include all five resource keys.");
            }
            normalizeResourcePool(stage.production);
          });
        } catch (error) {
          return setupFailure(
            "INVALID_MATCH_SETUP",
            error instanceof Error ? error.message : "Invalid Land production.",
            `catalog.cards.${index}.stages`,
          );
        }
      }
    } else if (
      (input.catalog.schemaVersion === 2 ||
        input.catalog.schemaVersion === 3 ||
        input.catalog.schemaVersion === 4) &&
      definition.type === "army"
    ) {
      if (
        !("upkeep" in definition) ||
        !Number.isSafeInteger(definition.armySize) ||
        definition.armySize <= 0 ||
        !Number.isSafeInteger(definition.attack) ||
        definition.attack < 0 ||
        !Number.isSafeInteger(definition.guard) ||
        definition.guard < 0
      ) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Projected schema-2/3 Armies require non-negative printed combat stats and positive Size.",
          `catalog.cards.${index}`,
        );
      }
      try {
        if (!hasCompleteResourcePool(definition.upkeep)) {
          throw new RangeError("Army Upkeep must include all five resource keys.");
        }
        normalizeResourcePool(definition.upkeep);
      } catch (error) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          error instanceof Error ? error.message : "Invalid Army Upkeep.",
          `catalog.cards.${index}.upkeep`,
        );
      }
    } else if (input.catalog.schemaVersion === 3 && definition.type === "commander") {
      if (
        !("armyCapacity" in definition) ||
        !Number.isSafeInteger(definition.attack) ||
        definition.attack < 0 ||
        !Number.isSafeInteger(definition.guard) ||
        definition.guard < 0 ||
        !Number.isSafeInteger(definition.armyCapacity) ||
        definition.armyCapacity < 0 ||
        definition.armyCapacity > 5 ||
        !("mandates" in definition) ||
        !hasValidMandateReferences(definition.mandates)
      ) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Projected schema-3 Commanders require printed stats, Capacity at most five, and immutable Mandate references.",
          `catalog.cards.${index}`,
        );
      }
    } else if (input.catalog.schemaVersion === 4 && definition.type === "commander") {
      const schemaFourDefinition = definition as EngineCardDefinitionV4;
      if (schemaFourDefinition.type !== "commander") {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Schema-4 Commander projection is malformed.",
          `catalog.cards.${index}`,
        );
      }
      if (
        !Number.isSafeInteger(schemaFourDefinition.attack) ||
        schemaFourDefinition.attack < 0 ||
        !Number.isSafeInteger(schemaFourDefinition.guard) ||
        schemaFourDefinition.guard < 0 ||
        !hasValidMandateReferences(schemaFourDefinition.mandates)
      ) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Projected schema-4 Commanders require printed combat stats and immutable Mandate references.",
          `catalog.cards.${index}`,
        );
      }
    } else if (input.catalog.schemaVersion === 4 && definition.type === "event") {
      const schemaFourDefinition = definition as EngineCardDefinitionV4;
      if (schemaFourDefinition.type !== "event") {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Schema-4 Event projection is malformed.",
          `catalog.cards.${index}`,
        );
      }
      if (schemaFourDefinition.effects.length === 0) {
        return setupFailure(
          "INVALID_MATCH_SETUP",
          "Projected schema-4 Events require at least one declarative effect.",
          `catalog.cards.${index}.effects`,
        );
      }
      for (
        let effectIndex = 0;
        effectIndex < schemaFourDefinition.effects.length;
        effectIndex += 1
      ) {
        const effect = schemaFourDefinition.effects[effectIndex];
        if (effect === undefined) continue;
        try {
          if (effect.type === "draw-cards") {
            if (!Number.isSafeInteger(effect.count) || effect.count <= 0) {
              throw new RangeError("Event draw count must be a positive safe integer.");
            }
          } else {
            if (!hasCompleteResourcePool(effect.resources)) {
              throw new RangeError("Event resources must include all five resource keys.");
            }
            normalizeResourcePool(effect.resources);
          }
        } catch (error) {
          return setupFailure(
            "INVALID_MATCH_SETUP",
            error instanceof Error ? error.message : "Invalid Event effect.",
            `catalog.cards.${index}.effects.${effectIndex}`,
          );
        }
      }
    }
  }

  return null;
}

function validateDecks(input: MatchSetupInput): MatchSetupFailure | null {
  const definitions = definitionMap(input.catalog);

  for (const playerId of PLAYER_IDS) {
    const deck = input.decks[playerId];
    if (deck.deckId.length === 0 || deck.cardDefinitionIds.length !== REQUIRED_SETUP_DECK_SIZE) {
      return setupFailure(
        "INVALID_DECK",
        `Each setup deck must contain exactly ${REQUIRED_SETUP_DECK_SIZE} cards.`,
        `decks.${playerId}.cardDefinitionIds`,
        { playerId, receivedCount: deck.cardDefinitionIds.length },
      );
    }

    const copies = new Map<CardDefinitionId, number>();
    let landCount = 0;
    for (let index = 0; index < deck.cardDefinitionIds.length; index += 1) {
      const definitionId = deck.cardDefinitionIds[index];
      if (definitionId === undefined) {
        return setupFailure(
          "INVALID_DECK",
          "Deck entries cannot be sparse.",
          `decks.${playerId}.cardDefinitionIds.${index}`,
        );
      }

      const definition = definitions.get(definitionId);
      if (definition === undefined) {
        return setupFailure(
          "CARD_DEFINITION_NOT_FOUND",
          `Deck references unknown card definition '${definitionId}'.`,
          `decks.${playerId}.cardDefinitionIds.${index}`,
          { playerId, cardDefinitionId: definitionId },
        );
      }

      const nextCount = (copies.get(definitionId) ?? 0) + 1;
      copies.set(definitionId, nextCount);
      if (nextCount > MAX_SETUP_CARD_COPIES) {
        return setupFailure(
          "INVALID_DECK",
          `A setup deck may contain at most ${MAX_SETUP_CARD_COPIES} copies of one definition.`,
          `decks.${playerId}.cardDefinitionIds.${index}`,
          { playerId, cardDefinitionId: definitionId, copies: nextCount },
        );
      }

      if (definition.type === "land") landCount += 1;
    }

    if (landCount === 0) {
      return setupFailure(
        "INVALID_DECK",
        "A setup deck must contain at least one Land (temporary FK-RULE-002 policy).",
        `decks.${playerId}.cardDefinitionIds`,
        { playerId, landCount },
      );
    }
  }

  return null;
}

/** Stable identity uses original deck position and never shuffled order. */
export function createCardInstanceId(
  matchId: string,
  playerId: PlayerId,
  originalDeckOrdinal: number,
  cardDefinitionId: CardDefinitionId,
): CardInstanceId {
  return `card:${matchId.length}:${matchId}:${playerId}:${originalDeckOrdinal}:${cardDefinitionId}`;
}

function instantiateDeck(
  input: MatchSetupInput,
  playerId: PlayerId,
  manifest: Record<CardInstanceId, CardInstanceRecord>,
): readonly CardInstanceId[] | MatchSetupFailure {
  return input.decks[playerId].cardDefinitionIds.map((cardDefinitionId, originalDeckOrdinal) => {
    const cardInstanceId = createCardInstanceId(
      input.matchId,
      playerId,
      originalDeckOrdinal,
      cardDefinitionId,
    );
    if (manifest[cardInstanceId] !== undefined) {
      throw setupFailure(
        "DUPLICATE_CARD_INSTANCE_ID",
        `Generated duplicate card instance '${cardInstanceId}'.`,
        `decks.${playerId}.cardDefinitionIds.${originalDeckOrdinal}`,
        { cardInstanceId },
      );
    }
    manifest[cardInstanceId] = {
      cardInstanceId,
      cardDefinitionId,
      ownerPlayerId: playerId,
      originalDeckOrdinal,
    };
    return cardInstanceId;
  });
}

function handContainsLand(
  hand: readonly CardInstanceId[],
  manifest: Readonly<Record<CardInstanceId, CardInstanceRecord>>,
  catalog: EngineCardCatalog,
): boolean {
  const definitions = definitionMap(catalog);
  return hand.some((cardInstanceId) => {
    const instance = manifest[cardInstanceId];
    return instance !== undefined && definitions.get(instance.cardDefinitionId)?.type === "land";
  });
}

function createOpeningHand(
  playerId: PlayerId,
  unshuffledDeck: readonly CardInstanceId[],
  random: SeededRandomState,
  manifest: Readonly<Record<CardInstanceId, CardInstanceRecord>>,
  catalog: EngineCardCatalog,
  events: GameEvent[],
): OpeningHandResult | OpeningHandFailure {
  let deck = unshuffledDeck;
  let currentRandom = random;
  const rejectedHands: CardInstanceId[][] = [];

  for (let attempt = 1; attempt <= MAX_OPENING_HAND_ATTEMPTS; attempt += 1) {
    const shuffled = shuffleDeterministically(deck, currentRandom);
    deck = shuffled.value;
    currentRandom = shuffled.state;
    events.push({
      type: "deck-shuffled",
      eventId: setupEventId(events.length),
      actionSequence: 0,
      playerId,
      purpose: attempt === 1 ? "opening-hand" : "opening-hand-redraw",
      attempt,
      cardInstanceIds: deck,
    });

    const hand = deck.slice(0, 5);
    const remainingDeck = deck.slice(5);
    events.push({
      type: "opening-hand-drawn",
      eventId: setupEventId(events.length),
      actionSequence: 0,
      playerId,
      attempt,
      cardInstanceIds: hand,
    });

    if (handContainsLand(hand, manifest, catalog)) {
      return {
        ok: true,
        deck: remainingDeck,
        hand,
        audit: {
          acceptedCardInstanceIds: hand,
          drawAttempts: attempt,
          rejectedHands,
        },
        random: currentRandom,
      };
    }

    rejectedHands.push([...hand]);
    events.push({
      type: "opening-hand-rejected",
      eventId: setupEventId(events.length),
      actionSequence: 0,
      playerId,
      attempt,
      cardInstanceIds: hand,
      reason: "no-land",
    });
    events.push({
      type: "opening-hand-returned",
      eventId: setupEventId(events.length),
      actionSequence: 0,
      playerId,
      attempt,
      cardInstanceIds: hand,
    });

    // Index zero is the deck top; restoring hand first recreates the full
    // pre-draw shuffled sequence before the next complete reshuffle.
    deck = [...hand, ...remainingDeck];
  }

  return {
    ok: false,
    failure: setupFailure(
      "OPENING_HAND_REDRAW_LIMIT_EXCEEDED",
      "The defensive opening-hand redraw ceiling was exceeded without changing the hand rule.",
      `decks.${playerId}`,
      { playerId, attempts: MAX_OPENING_HAND_ATTEMPTS },
    ),
  };
}

export function createMatch(input: MatchSetupInput): CreateMatchResult {
  const catalogFailure = validateCatalog(input);
  if (catalogFailure !== null) return { ok: false, failure: catalogFailure };
  const deckFailure = validateDecks(input);
  if (deckFailure !== null) return { ok: false, failure: deckFailure };

  const initialRandomState = createSeededRandom(input.seed);
  const events: GameEvent[] = [
    {
      type: "match-setup-started",
      eventId: setupEventId(0),
      actionSequence: 0,
      matchId: input.matchId,
      seed: input.seed,
      gameplayCatalogHash: input.gameplayCatalogHash,
    },
  ];
  const manifest: Record<CardInstanceId, CardInstanceRecord> = {};
  let playerOneDeck: readonly CardInstanceId[];
  let playerTwoDeck: readonly CardInstanceId[];

  try {
    const first = instantiateDeck(input, "player-1", manifest);
    const second = instantiateDeck(input, "player-2", manifest);
    if (!Array.isArray(first) || !Array.isArray(second)) {
      return { ok: false, failure: first as MatchSetupFailure };
    }
    playerOneDeck = first;
    playerTwoDeck = second;
  } catch (error) {
    if (typeof error === "object" && error !== null && "code" in error) {
      return { ok: false, failure: error as MatchSetupFailure };
    }
    throw error;
  }

  events.push({
    type: "deck-instantiated",
    eventId: setupEventId(events.length),
    actionSequence: 0,
    playerId: "player-1",
    deckId: input.decks["player-1"].deckId,
    cardInstanceIds: playerOneDeck,
  });
  events.push({
    type: "deck-instantiated",
    eventId: setupEventId(events.length),
    actionSequence: 0,
    playerId: "player-2",
    deckId: input.decks["player-2"].deckId,
    cardInstanceIds: playerTwoDeck,
  });

  const playerOneOpening = createOpeningHand(
    "player-1",
    playerOneDeck,
    initialRandomState,
    manifest,
    input.catalog,
    events,
  );
  if (!playerOneOpening.ok) return playerOneOpening;
  const playerTwoOpening = createOpeningHand(
    "player-2",
    playerTwoDeck,
    playerOneOpening.random,
    manifest,
    input.catalog,
    events,
  );
  if (!playerTwoOpening.ok) return playerTwoOpening;

  const startingPlayerDraw = nextRandomInt(playerTwoOpening.random, PLAYER_IDS.length);
  const startingPlayerId = PLAYER_IDS[startingPlayerDraw.value];
  if (startingPlayerId === undefined) {
    return {
      ok: false,
      failure: setupFailure(
        "INVALID_MATCH_SETUP",
        "Seeded starting-player selection produced an invalid player index.",
        "seed",
      ),
    };
  }
  events.push({
    type: "starting-player-selected",
    eventId: setupEventId(events.length),
    actionSequence: 0,
    playerId: startingPlayerId,
  });

  const finalSetupRandomState = startingPlayerDraw.state;
  events.push({
    type: "match-created",
    eventId: setupEventId(events.length),
    actionSequence: 0,
    matchId: input.matchId,
    startingPlayerId,
    randomDraws: finalSetupRandomState.draws,
  });

  const commonState: Omit<GameStateV2, "schemaVersion" | "catalog"> = {
    rulesVersion: input.rulesVersion,
    cardDataVersion: input.cardDataVersion,
    matchId: input.matchId,
    seed: input.seed,
    random: finalSetupRandomState,
    turnNumber: 1,
    activePlayerId: startingPlayerId,
    phase: "ready" as const,
    players: {
      "player-1": createPlayer("player-1", playerOneOpening.deck, playerOneOpening.hand),
      "player-2": createPlayer("player-2", playerTwoOpening.deck, playerTwoOpening.hand),
    },
    lanes: {
      left: {
        laneId: LANE_IDS[0],
        result: "active",
        players: { "player-1": emptyLaneSide(), "player-2": emptyLaneSide() },
      },
      center: {
        laneId: LANE_IDS[1],
        result: "active",
        players: { "player-1": emptyLaneSide(), "player-2": emptyLaneSide() },
      },
      right: {
        laneId: LANE_IDS[2],
        result: "active",
        players: { "player-1": emptyLaneSide(), "player-2": emptyLaneSide() },
      },
    },
    actionLog: [],
    eventQueue: events,
    status: { type: "active" as const },
    cardInstances: manifest,
    setup: {
      gameplayCatalogHash: input.gameplayCatalogHash,
      startingPlayerId,
      initialRandomState,
      finalSetupRandomState,
      openingHands: {
        "player-1": playerOneOpening.audit,
        "player-2": playerTwoOpening.audit,
      },
    },
    firstTurnDrawStatus: { "player-1": "pending" as const, "player-2": "pending" as const },
  };
  const stateCatalog = cloneEngineCardCatalog(input.catalog);
  if (input.rulesVersion === SUPPORTED_FLANK_RULES_VERSION && stateCatalog.schemaVersion === 1) {
    return {
      ok: false,
      failure: setupFailure(
        "INVALID_MATCH_SETUP",
        "The Flank and Kingdom Complete revision requires a schema-2 or schema-3 catalog.",
        "catalog.schemaVersion",
        { requiredCatalogSchemaVersions: "2,3" },
      ),
    };
  }
  if (
    input.rulesVersion === SUPPORTED_PUBLIC_BETA_RULES_VERSION &&
    stateCatalog.schemaVersion !== 4
  ) {
    return {
      ok: false,
      failure: setupFailure(
        "INVALID_MATCH_SETUP",
        "The public-beta action-window revision requires a schema-4 catalog.",
        "catalog.schemaVersion",
        { requiredCatalogSchemaVersion: 4 },
      ),
    };
  }
  if (
    input.rulesVersion !== SUPPORTED_PUBLIC_BETA_RULES_VERSION &&
    stateCatalog.schemaVersion === 4
  ) {
    return {
      ok: false,
      failure: setupFailure(
        "INVALID_MATCH_SETUP",
        "A schema-4 catalog is reserved for the public-beta action-window rules.",
        "catalog.schemaVersion",
      ),
    };
  }

  let state: SetupGameState;
  if (stateCatalog.schemaVersion === 1) {
    state = { ...commonState, schemaVersion: 2, catalog: stateCatalog };
  } else if (
    input.rulesVersion === SUPPORTED_PUBLIC_BETA_RULES_VERSION &&
    stateCatalog.schemaVersion === 4
  ) {
    state = {
      ...commonState,
      schemaVersion: 5,
      rulesVersion: PUBLIC_BETA_RULES_VERSION,
      catalog: stateCatalog,
      phase: "action-window",
      firstTurnDrawStatus: {
        ...commonState.firstTurnDrawStatus,
        [startingPlayerId]: "consumed",
      },
      supplyStatus: {
        type: "resolved",
        playerId: startingPlayerId,
        turnNumber: 1,
      },
      kingdomProgress: {
        "player-1": { populatedLaneIds: [], complete: false },
        "player-2": { populatedLaneIds: [], complete: false },
      },
      noTerritoryWarning: null,
    } satisfies GameStateV5;
  } else if (stateCatalog.schemaVersion === 2 || stateCatalog.schemaVersion === 3) {
    state =
      input.rulesVersion === SUPPORTED_FLANK_RULES_VERSION
        ? ({
            ...commonState,
            schemaVersion: 4,
            catalog: stateCatalog,
            supplyStatus: { type: "not-started" },
            kingdomProgress: {
              "player-1": { populatedLaneIds: [], complete: false },
              "player-2": { populatedLaneIds: [], complete: false },
            },
            noTerritoryWarning: null,
          } satisfies GameStateV4)
        : {
            ...commonState,
            schemaVersion: 3,
            catalog: stateCatalog,
            supplyStatus: { type: "not-started" },
          };
  } else {
    throw new Error("Validated setup reached an incompatible catalog/rules combination.");
  }

  if (state.schemaVersion === 5) {
    events.push(
      {
        type: "turn-started",
        eventId: setupEventId(events.length),
        actionSequence: 0,
        playerId: startingPlayerId,
        turnNumber: 1,
      },
      {
        type: "draw-skipped",
        eventId: setupEventId(events.length + 1),
        actionSequence: 0,
        playerId: startingPlayerId,
        reason: "opening-hand-used-for-first-turn",
      },
      {
        type: "supply-started",
        eventId: setupEventId(events.length + 2),
        actionSequence: 0,
        playerId: startingPlayerId,
        turnNumber: 1,
      },
      {
        type: "supply-resolved",
        eventId: setupEventId(events.length + 3),
        actionSequence: 0,
        playerId: startingPlayerId,
        turnNumber: 1,
        kingdomPool: createEmptyResourcePool(),
      },
      {
        type: "action-window-opened",
        eventId: setupEventId(events.length + 4),
        actionSequence: 0,
        playerId: startingPlayerId,
        turnNumber: 1,
      },
    );
  }

  const invariantViolation = getStateInvariantViolations(state)[0];
  if (invariantViolation !== undefined) {
    return {
      ok: false,
      failure: setupFailure(
        "INVALID_MATCH_SETUP",
        invariantViolation.message,
        invariantViolation.path,
        { invariantCode: invariantViolation.code },
      ),
    };
  }

  return {
    ok: true,
    state,
    events,
    initialStateHash: getStateHash(state),
    initialRandomState,
    finalRandomState: finalSetupRandomState,
    startingPlayerId,
    gameplayCatalogHash: input.gameplayCatalogHash,
  };
}
